import React from 'react';
import { Button, Tooltip } from '@mui/material';
import { Visibility, ShoppingCart } from '@mui/icons-material';
import DataTable, { StatusColumn } from '../DataTable';

export interface QuoteData {
  id: string;
  client: string;
  number: string;
  date: string;
  status: 'Draft' | 'Submitted' | 'Approved' | 'Rejected' | 'Analise de Credito';
  total: string;
  [key: string]: unknown;
}

interface QuotesTableProps {
  data: QuoteData[];
  className?: string;
  onViewQuote: (quoteId: string) => void;
  onEditQuote?: (quoteId: string) => void;
  onConvertQuote: (quoteId: string) => void;
}

const QuotesTable: React.FC<QuotesTableProps> = ({
  data,
  className,
  onViewQuote,
  onEditQuote,
  onConvertQuote,
}) => {
  const columns: StatusColumn[] = [
    {
      id: 'client',
      label: 'Cliente',
      minWidth: 180,
    },
    {
      id: 'number',
      label: 'Número da Cotação',
      minWidth: 150,
    },
    {
      id: 'date',
      label: 'Criado em',
      minWidth: 120,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      renderStatus: true,
      statusVariants: {
        Draft: { backgroundColor: '#e3f2fd', textColor: '#2196f3' },
        Submitted: { backgroundColor: '#fffdf0', textColor: '#e6a816' },
        Approved: { backgroundColor: '#fafff6', textColor: '#356e01' },
        Rejected: { backgroundColor: '#ffdee7', textColor: '#da4865' },
      },
      format: (value: unknown) => {
        const status = String(value || '');
        const statusMap: Record<string, string> = {
          Draft: 'Rascunho',
          Submitted: 'Enviado',
          Approved: 'Aprovado',
          Rejected: 'Rejeitado',
          'Analise de Credito': 'Análise de Crédito',
        };
        return statusMap[status] || status;
      },
    },
    {
      id: 'total',
      label: 'Total Estimado (R$)',
      minWidth: 150,
    },
    {
      id: 'id',
      label: 'Ações',
      minWidth: 280,
      format: (value: unknown, row?: QuoteData) => {
        // Encontrar o status da cotação na linha atual
        const quoteStatus = row?.status;
        const isApproved = quoteStatus === 'Approved';

        // Tooltip message for disabled button
        const getTooltipMessage = () => {
          if (!isApproved) {
            return 'Apenas cotações aprovadas podem ser transformadas em pedido';
          }
          return '';
        };

        return (
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              color="primary"
              size="small"
              onClick={e => {
                e.stopPropagation(); // Prevent row click when clicking the button
                onViewQuote(value as string);
              }}
              startIcon={<Visibility sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none',
                color: '#4460ff',
                fontWeight: 'normal',
                fontSize: '0.875rem',
                padding: '2px 8px',
              }}
            >
              Visualizar
            </Button>

            <Tooltip title={getTooltipMessage()} arrow>
              <span>
                <Button
                  color="primary"
                  size="small"
                  disabled={!isApproved}
                  onClick={e => {
                    e.stopPropagation(); // Prevent row click when clicking the button
                    if (isApproved) {
                      onConvertQuote(value as string);
                    }
                  }}
                  startIcon={<ShoppingCart sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: 'none',
                    color: isApproved ? '#4460ff' : '#bbb',
                    fontWeight: 'normal',
                    fontSize: '0.875rem',
                    padding: '2px 8px',
                    '&.Mui-disabled': {
                      color: '#bbb',
                    },
                  }}
                >
                  Transformar em pedido
                </Button>
              </span>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={data}
      statusField="status"
      containerClassName={className}
      fullWidth
      onRowClick={row => onViewQuote(row.id as string)}
    />
  );
};

export default QuotesTable;
